import { monitorApi } from '../../api/monitor';
import type { ScheduledJob } from '../../api/monitor';
import { ErrorCard, LoadingCard } from '../../components/ui/ScreenFallback';
import { Toast, useToast } from '../../components/ui/Toast';
import { useJobs } from '../../hooks/useJobs';
import { syncSummary } from '../../lib/lines';
import { JobCard } from './JobCard';
import { RunHistory } from './RunHistory';
import { SyncLineCard } from './SyncLineCard';

interface JobsScreenProps {
  /** Recarrega o sino: toda ação daqui vira notificação. */
  onNotified?: () => void;
}

/**
 * Tela de Serviços assíncronos: o que roda sozinho no SICOP, e o que dá para
 * mandar rodar agora.
 *
 * São três leituras da mesma coisa. Em cima, um card por job agendado — o que
 * faz, com que frequência, como terminou da última vez e o botão de executar.
 * Depois, a sincronização de uma linha avulsa, que é a mesma pergunta ("roda
 * isso agora") com escopo de um número só. Embaixo, o histórico de
 * `service_runs`, que é onde se lê o motivo de cada falha.
 *
 * Não há fila nem worker por trás disso: cada serviço agenda os próprios jobs
 * com node-cron no boot. Por isso o horário é informativo e não editável —
 * mudar agendamento é mexer na variável de ambiente do serviço e reiniciá-lo.
 */
export function JobsScreen({ onNotified }: JobsScreenProps) {
  const controller = useJobs();
  const { state, runs, loading, error, runsError, triggering } = controller;
  const { toast, show } = useToast();

  const handleRunNow = async (job: ScheduledJob) => {
    try {
      await controller.runNow(job.job);
      // A rodada do eagle segue em andamento depois desta resposta; quem conta o
      // fim é a notificação, não este aviso.
      show(`${job.label}: rodada disparada.`);
    } catch (cause) {
      show((cause as Error).message, 'error');
    } finally {
      onNotified?.();
    }
  };

  const handleSyncLine = async (line: string) => {
    try {
      const { results } = await monitorApi.syncNumberByLine(line);
      const { text, kind } = syncSummary(results);
      show(text, kind);
    } catch (cause) {
      show((cause as Error).message, 'error');
    } finally {
      onNotified?.();
    }
  };

  if (loading && !state) {
    return <LoadingCard message="Carregando os serviços assíncronos…" />;
  }

  if (error || !state) {
    return (
      <ErrorCard
        title="Não foi possível carregar os serviços assíncronos."
        detail={error}
        onRetry={() => void controller.reload()}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        {state.jobs.map((job) => (
          <JobCard
            key={`${job.service}:${job.job}`}
            job={job}
            triggering={triggering === job.job}
            onRunNow={(target) => void handleRunNow(target)}
            onSeeRuns={(target) => controller.setJobFilter(target.job)}
          />
        ))}

        <SyncLineCard onSync={handleSyncLine} />
      </div>

      <div className="text-ink-400 mt-2.5 text-[11.5px]">
        Horários no fuso {state.timezone}. O agendamento vem da configuração de cada serviço:
        para mudá-lo, altere a variável de ambiente e reinicie o serviço.
      </div>

      <RunHistory
        page={runs}
        jobs={state.jobs}
        jobFilter={controller.jobFilter}
        statusFilter={controller.statusFilter}
        error={runsError}
        onJobChange={controller.setJobFilter}
        onStatusChange={controller.setStatusFilter}
        onPageChange={controller.goToPage}
      />

      <Toast message={toast} />
    </>
  );
}
